// frontend/src/services/api.js

const BASE = "/api";


// ---------------------------------------------------------
// Shared request helpers
// ---------------------------------------------------------

const CSRF_HEADER = {
  "X-CSRF-Protection": "1",
};

function jsonHeaders() {
  return {
    "Content-Type": "application/json",
    ...CSRF_HEADER,
  };
}


// ---------------------------------------------------------
// Error handling
// ---------------------------------------------------------

async function parseError(res) {
  try {
    const contentType = res.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const data = await res.json();

      if (data?.detail) {
        return data.detail;
      }

      return `HTTP ${res.status}`;
    }

    const text = await res.text();
    return text || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}


async function handleFetch(res) {
  if (!res.ok) {
    throw new Error(await parseError(res));
  }

  const contentType = res.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return res.json();
  }

  return res.text();
}


// ---------------------------------------------------------
// Authentication
// ---------------------------------------------------------

// Auth: who am I?
export async function authMe() {
  try {
    const res = await fetch(`${BASE}/auth/me`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) {
      const err = new Error("Not logged in");
      err.status = res.status;
      throw err;
    }

    return res.json();

  } catch (e) {

    // Network/offline errors end up here (no res/status).
    if (
      e &&
      typeof e === "object" &&
      !("status" in e)
    ) {
      e.status = undefined;
    }

    throw e;
  }
}


// Login does not require an existing authenticated session,
// but sending the CSRF header is harmless and keeps POST
// behaviour consistent.
export async function authLogin(payload) {
  const res = await fetch(`${BASE}/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });

  return handleFetch(res);
}


// Logout
export async function authLogout() {
  const res = await fetch(`${BASE}/auth/logout`, {
    method: "POST",
    credentials: "include",
    headers: CSRF_HEADER,
  });

  return handleFetch(res);
}


// Change password
export async function authChangePassword(payload) {
  const res = await fetch(`${BASE}/auth/change-password`, {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });

  return handleFetch(res);
}


// ---------------------------------------------------------
// Questionnaires
// ---------------------------------------------------------

export async function createQuestionnaire(data) {
  const res = await fetch(`${BASE}/questionnaires`, {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({ data }),
  });

  return handleFetch(res);
}


export async function updateQuestionnaire(id, data) {
  const res = await fetch(`${BASE}/questionnaires/${id}`, {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({ data }),
  });

  return handleFetch(res);
}


export async function getQuestionnaire(id) {
  const res = await fetch(`${BASE}/questionnaires/${id}`, {
    method: "GET",
    credentials: "include",
  });

  return handleFetch(res);
}


export async function finalizeQuestionnaire(id) {
  const res = await fetch(
    `${BASE}/questionnaires/${id}/finalize`,
    {
      method: "POST",
      credentials: "include",
      headers: CSRF_HEADER,
    }
  );

  return handleFetch(res);
}


export async function listQuestionnaires(options = {}) {
  const cacheBust = options.cacheBust || Date.now();

  const res = await fetch(
    `${BASE}/questionnaires?t=${cacheBust}`,
    {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache",
      },
    }
  );

  return handleFetch(res);
}


export async function downloadQuestionnairePdf(id) {
  const res = await fetch(
    `${BASE}/questionnaires/${id}/pdf`,
    {
      method: "POST",
      credentials: "include",
      headers: CSRF_HEADER,
    }
  );

  if (!res.ok) {
    throw new Error(await res.text());
  }

  return res.blob();
}


export async function deleteQuestionnaire(id) {
  const res = await fetch(
    `${BASE}/questionnaires/${id}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: CSRF_HEADER,
    }
  );

  return handleFetch(res);
}


// ---------------------------------------------------------
// Admin - users
// ---------------------------------------------------------

export async function adminUsersList() {
  const res = await fetch(`${BASE}/admin/users`, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error("Failed to load users");
  }

  return res.json();
}


// Admin create user
export async function adminUserCreate(payload) {
  const res = await fetch(`${BASE}/admin/users`, {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });

  return handleFetch(res);
}


export async function adminUserSetRole(userId, role) {
  const res = await fetch(
    `${BASE}/admin/users/${userId}/role`,
    {
      method: "PATCH",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify({ role }),
    }
  );

  if (!res.ok) {
    throw new Error("Failed to update role");
  }

  return res.json();
}


export async function adminUserDelete(userId) {
  const res = await fetch(
    `${BASE}/admin/users/${userId}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: CSRF_HEADER,
    }
  );

  if (!res.ok) {
    throw new Error("Failed to delete user");
  }

  return res.json();
}


// ---------------------------------------------------------
// Admin - questionnaires
// ---------------------------------------------------------

export async function adminQuestionnaireSchema() {
  const res = await fetch(
    `${BASE}/admin/questionnaires/schema`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!res.ok) {
    throw new Error("Failed to load schema");
  }

  return res.json();
}


export async function adminQuestionnaireSearch(payload) {
  const res = await fetch(
    `${BASE}/admin/questionnaires/search`,
    {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!res.ok) {
    throw new Error("Search failed");
  }

  return res.json();
}


export async function adminQuestionnaireExport(payload) {
  const res = await fetch(
    `${BASE}/admin/questionnaires/export`,
    {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!res.ok) {
    throw new Error("Export failed");
  }

  return res.blob();
}


export async function adminExportOptions() {
  const res = await fetch(
    `${BASE}/admin/export/options`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!res.ok) {
    throw new Error(await res.text());
  }

  return res.json();
}


export async function adminExportJson(params = {}) {
  const qs = new URLSearchParams(params).toString();

  const res = await fetch(
    `${BASE}/admin/export/json?${qs}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!res.ok) {
    throw new Error(await res.text());
  }

  return res.blob();
}


export async function adminExportCsv(params = {}) {
  const qs = new URLSearchParams(params).toString();

  const res = await fetch(
    `${BASE}/admin/export/csv?${qs}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!res.ok) {
    throw new Error(await res.text());
  }

  return res.blob();
}


// ---------------------------------------------------------
// Tech team
// ---------------------------------------------------------

export async function generateTechTeamReport({
  pdf,
  reportType,
  expertName,
  caseConsiderations = [],
  interpretationJson = "",
  overrideValues = {},
}) {
  const formData = new FormData();

  formData.append("pdf", pdf);
  formData.append("report_type", reportType);
  formData.append("expert_name", expertName);

  formData.append(
    "case_considerations",
    caseConsiderations.join("|")
  );

  formData.append(
    "interpretation_json",
    interpretationJson || ""
  );

  formData.append(
    "override_values",
    JSON.stringify(overrideValues || {})
  );

  const res = await fetch(
    `${BASE}/tech-team/generate-report`,
    {
      method: "POST",
      credentials: "include",

      // Do NOT set Content-Type manually for FormData.
      // The browser adds the multipart boundary.
      headers: CSRF_HEADER,

      body: formData,
    }
  );

  if (!res.ok) {
    throw new Error(await res.text());
  }

  return res.blob();
}


export async function generateBloodFixPdf({
  reportPdf,
  bloodPdf,
  bloodTestMode,
  includeMcv,
  outputStyle = "classic",
}) {
  const mode =
    bloodTestMode ||
    (
      includeMcv
        ? "bloods_with_mcv"
        : "bloods_no_mcv"
    );

  const formData = new FormData();

  if (reportPdf) {
    formData.append(
      "report_pdf",
      reportPdf
    );
  }

  formData.append(
    "blood_pdf",
    bloodPdf
  );

  formData.append(
    "blood_test_mode",
    mode
  );

  formData.append(
    "output_style",
    outputStyle
  );

  // Backwards compatibility with old backend logic.
  formData.append(
    "include_mcv",
    (
      mode === "bloods_with_mcv" ||
      mode === "mcv_only"
    )
      ? "true"
      : "false"
  );

  const res = await fetch(
    `${BASE}/tech-team/blood-fix`,
    {
      method: "POST",
      credentials: "include",
      headers: CSRF_HEADER,
      body: formData,
    }
  );

  if (!res.ok) {
    throw new Error(await res.text());
  }

  return res.blob();
}

export async function advancedTechTeamReport({pdf,expertName,caseConsiderations=[],interpretationJson="",overrideValues={},approval=null}) {
  const form=new FormData();
  form.append("pdf",pdf);
  form.append("expert_name",expertName);
  form.append("case_considerations",caseConsiderations.join("|"));
  form.append("interpretation_json",interpretationJson);
  form.append("override_values",JSON.stringify(overrideValues));
  if (approval) form.append("advanced_approval",JSON.stringify(approval));
  const action=approval?"advanced-generate":"advanced-preview";
  const res=await fetch(`${BASE}/tech-team/${action}`,{method:"POST",credentials:"include",headers:CSRF_HEADER,body:form});
  if (!res.ok) throw new Error(await res.text());
  return approval?res.blob():res.json();
}
